import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import MarkDown from '~/components/Helpers/MarkDown'
import Breadcrumbs from '~/components/UI/General/Breadcrumbs/Breadcrumbs'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Image from '~/components/Helpers/Image'
import Badge from '~/components/UI/General/Badge/Badge'
import IntroText from '~/components/Text/IntroText/IntroText'
import PrimaryCTA from '~/components/UI/Buttons/PrimaryCTA/PrimaryCTA'
import SecondaryCTA from '~/components/UI/Buttons/SecondaryCTA/SecondaryCTA'
import SearchAutocomplete from '~/components/Content/SearchAutocomplete/SearchAutocomplete'
import ArrowDown from './icon/arrow-down'

import { useSearchAutocomplete } from '~/hooks/useAutocompleteSearch'

const heroVariants = cva('relative', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      chartreuse: 'bg-char text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d dark:bg-black dark:text-white',
      white: 'bg-white dark:bg-black'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

const HeroStandard = ({
  data,
  breadcrumb,
  imageBackground,
  className
}: HeroProps) => {
  const {
    heading,
    eyebrow,
    backgroundColour,
    userAction,
    introParagraph,
    image,
    video,
    badge,
    search
  } = data
  const size = data?.image ? data?.size : 'small'
  const texture = data?.urbanTexture
    ? data?.urbanTexture.asset.sys.uri
    : '/image-library/textures/urban/UoH-Texture-Urban-01b.jpg'

  const primaryButton = data?.buttons[0] || null
  const secondaryButton = data?.buttons[1] || null

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  const previewUrl =
    'https://preview-websitemodern-uni-hull.cloud.contensis.com'

  const scrollDown = () => {
    document
      // .querySelector('main')
      .querySelectorAll('section')[1]
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Updated search prop to match SearchItem[] type
  const updatedSearch = search?.map(item => ({
    ...item,
    contentType: Array.isArray(item.contentType)
      ? item.contentType
      : Array.isArray(item.contentType?.id)
        ? item.contentType.id.map(id => ({ id }))
        : []
  }))

  const { mappedItems, isSuccess, handleQueryChange } =
    useSearchAutocomplete(updatedSearch)

  return (
    <section
      className={cn(
        heroVariants({ backgroundColour })
        // 'pt-[70px] sm:pt-[90px]'
      )}
    >
      <div
        className={cn(
          'gap-x-l gap-y-m px-xs-plus pt-xs md:pt-m lg:gap-x-2xl lg:gap-y-l relative z-1 flex flex-col lg:container',
          // Medium & large only
          size !== 'small'
            ? 'md:pl-l lg:pt-xl md:grid md:grid-cols-2 md:grid-rows-[auto_1fr]'
            : '',
          // Medium only
          size === 'medium' ? 'lg:pb-2xl lg:grid-cols-[2fr_1fr]' : '',
          // Small only
          size === 'small' ? 'pb-l md:px-l md:pb-xl lg:pb-2xl lg:pt-xl' : '',
          className
        )}
      >
        <div
          className={cn(
            'space-y-m lg:space-y-l',
            // Medium & large only
            size !== 'small'
              ? 'h-full md:col-span-2 md:row-start-1 xl:col-span-1'
              : ''
          )}
        >
          <Breadcrumbs
            data={breadcrumb}
            colour={foregroundColour}
            className='max-sm:mb-s'
          />
          <div className='space-y-xs lg:space-y-xs-plus'>
            {eyebrow && <div className='tag'>{eyebrow}</div>}
            <MarkDown
              content={'# ' + heading}
              className={cn(
                'text-balance',
                size === 'small' ? 'max-w-max' : ''
              )}
            />
          </div>
        </div>

        <div
          className={cn(
            'space-y-l',
            // Medium & large only
            size !== 'small' ? 'md:mb-m md:row-start-2' : '',
            // Large only
            size === 'large' ? 'pb-2xs-plus md:pb-2xl' : '',
            // Medium only
            size === 'medium' ? 'md:pb-l' : ''
          )}
        >
          {introParagraph && introParagraph.length > 0 && (
            <IntroText
              content={introParagraph[0]}
              layoutColour={backgroundColour}
            />
          )}
          {userAction === 'buttons' && primaryButton && (
            <div className='gap-xs lg:gap-s flex flex-wrap'>
              <PrimaryCTA data={primaryButton} colour={foregroundColour} />
              {secondaryButton && (
                <SecondaryCTA
                  data={secondaryButton}
                  colour={foregroundColour}
                />
              )}
            </div>
          )}
          {userAction === 'search' && search && search.length > 0 && (
            <SearchAutocomplete
              placeholder={search[0]?.placeholder || 'Search...'}
              data={{
                label: '',
                note: ''
              }}
              loading={isSuccess}
              items={mappedItems}
              onQueryChange={handleQueryChange}
              disabled={false}
              className='text-blue-d max-w-[480px]'
              type={backgroundColour === 'white' ? 'Minimal' : 'Default'}
            />
          )}
        </div>

        {/* Medium & large only */}
        {size !== 'small' && (
          <div
            className={cn(
              // 'gap-l grid grid-cols-[min-content_1fr] md:col-start-2 md:row-start-2 md:block md:self-end xl:row-span-2 xl:row-start-1',
              'gap-l grid grid-cols-[min-content_1fr] md:sticky md:top-[90px] md:col-start-2 md:row-start-2 md:block md:self-start xl:row-span-2 xl:row-start-1',
              // Medium only
              size === 'medium' ? 'xl:self-center' : ''
            )}
          >
            <button
              className='relative col-start-1 ml-[10px] flex md:hidden'
              onClick={scrollDown}
            >
              <div className='tag absolute top-[106px] origin-top-left -rotate-90 whitespace-nowrap sm:text-[12px]'>
                Learn more
              </div>
              <ArrowDown />
            </button>
            <div
              className={cn(
                'relative col-start-2',
                // Medium only
                size === 'medium' ? '-mb-m -mr-xs-plus lg:mr-0 lg:mb-0' : ''
              )}
            >
              {badge && badge.length > 0 && (
                <Badge
                  className='-left-xs top-xs absolute z-1'
                  data={badge[0]}
                />
              )}
              <div
                className={cn(
                  'relative xl:mr-0',
                  // Large only
                  size === 'large'
                    ? '-mb-m -mr-xs-plus grow lg:-mb-[40px]'
                    : '',
                  // Medium only
                  size === 'medium'
                    ? 'md:mr-s w-[calc(100%-20px)] md:w-[calc(100%-48px)] lg:w-[calc(100%-32px)]'
                    : '',
                  imageBackground ? 'bg-grey-l' : ''
                )}
              >
                {video && image ? (
                  <BlockVideo
                    videoUrl={video?.sys.uri}
                    width={''}
                    height={''}
                    image={image}
                    className='aspect-square'
                    btnClassName='w-[36px] h-[36px] !p-2xs hover:bg-grey-l transition-colors'
                  />
                ) : (
                  image && (
                    <Image
                      image={image}
                      classes='aspect-square'
                      lazyLoad={false}
                      priority='high'
                    />
                  )
                )}

                {/* Medium only */}
                {size === 'medium' && (
                  <div
                    className={cn(
                      'w-xs-plus md:w-l lg:w-m -right-xs-plus md:-right-l lg:-right-m absolute bottom-0 z-[-1] h-[calc(100%-20px)] overflow-hidden md:h-[calc(100%-48px)] lg:h-[calc(100%-32px)]'
                    )}
                    style={{
                      backgroundImage: `url(${
                        isDevelopment && !texture.startsWith('https')
                          ? previewUrl
                          : ''
                      }${texture}?w=40&h=500&fit=crop&q=75&f=webp)`,
                      backgroundSize: 'cover',
                      opacity: 1
                    }}
                  ></div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Large only */}
      {size === 'large' && (
        <div
          className={cn(
            'h-xs-plus lg:h-m absolute bottom-0 z-0 w-full overflow-hidden'
          )}
          style={{
            backgroundImage: `url(${
              isDevelopment && !texture.startsWith('https') ? previewUrl : ''
            }${texture}?w=1080&h=40&fit=crop&q=75&f=webp)`,
            backgroundSize: 'cover',
            opacity: 1
          }}
        ></div>
      )}
    </section>
  )
}

export default HeroStandard
